import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowUpRight, Building, Microscope, Landmark, Stethoscope, Target } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const TEAL = "#00A8B3";
const GREEN = "#28A745";
const NAVY = "#1A1F36";
const MID = "#6B7280";

const kpis = [
  { metric: "% History Access per Consult", type: "North Star", target: "> 80%", status: "Baseline TBD", color: TEAL },
  { metric: "Avg. Context Load Time", type: "Performance", target: "< 5 sec", status: "Prototype: 3.2s", color: GREEN },
  { metric: "Patient Consent Rate", type: "Adoption", target: "> 70%", status: "Survey: 85%", color: TEAL },
  { metric: "Doctor Time Saved", type: "Efficiency", target: "> 2 min", status: "Est. 2.5 min", color: GREEN },
  { metric: "Clinic Retention", type: "Retention", target: "> 85%", status: "Target: Q1 2026", color: TEAL },
  { metric: "NPS Score", type: "Satisfaction", target: "> 50", status: "Survey: 62", color: GREEN },
];

const dist = [
  { icon: Building, label: "Small Clinics", desc: "Pilots with 2-5 doctor clinics in Mumbai & Tiruvallur", gradient: "linear-gradient(135deg, #00A8B3, #00838F)" },
  { icon: Microscope, label: "Diagnostic Labs", desc: "Seamless lab report integration partnerships", gradient: "linear-gradient(135deg, #28A745, #1B8A36)" },
  { icon: Landmark, label: "Govt. Hospitals", desc: "PHC/CHC integration via ABDM compliance", gradient: "linear-gradient(135deg, #0097A7, #006064)" },
  { icon: Stethoscope, label: "Doctor-Led Expansion", desc: "Peer referral viral loop through professional networks", gradient: "linear-gradient(135deg, #00A8B3, #28A745)" },
];

export const MetricsSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="metrics" className="py-20 lg:py-28 section-bold" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} className="text-center mb-12">
          <motion.span initial={{ opacity: 0, scale: 0.8 }} animate={inView ? { opacity: 1, scale: 1 } : {}}
            className="text-spaced mb-3 block" style={{ color: TEAL }}>M E T R I C S</motion.span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-4">Metrics & Distribution</h2>
        </motion.div>

        {/* KPI Table - dark glass */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.2 }} className="mb-14">
          <div className="relative rounded-2xl p-[2px] overflow-hidden" style={{
            background: `linear-gradient(135deg, ${TEAL}40, transparent 40%, transparent 60%, ${GREEN}40)`,
            backgroundSize: '200% 200%', animation: 'borderShift 6s ease infinite',
          }}>
            <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(12px)' }}>
              <div className="px-6 pt-6 pb-2 flex items-center gap-2">
                <Target className="w-5 h-5" style={{ color: TEAL }} />
                <h3 className="text-base font-heading font-bold text-white">Key Performance Indicators</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                      {["Metric", "Type", "Target", "Status"].map(h => (
                        <th key={h} className="px-6 py-3 text-left text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.4)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {kpis.map((r, i) => (
                      <motion.tr key={r.metric}
                        initial={{ opacity: 0, x: -20 }}
                        animate={inView ? { opacity: 1, x: 0 } : {}}
                        transition={{ delay: i * 0.08 + 0.4 }}
                        className="transition-colors"
                        style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,168,179,0.08)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <td className="px-6 py-4 text-sm font-medium text-white">{r.metric}</td>
                        <td className="px-6 py-4">
                          <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold" style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)' }}>{r.type}</span>
                        </td>
                        <td className="px-6 py-4 text-sm font-bold" style={{ color: r.color }}>{r.target}</td>
                        <td className="px-6 py-4 text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>{r.status}</td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Distribution Strategy - gradient cards */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.6 }}>
          <h3 className="text-lg font-heading font-bold text-white text-center mb-8">Distribution Strategy</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {dist.map((d, i) => (
              <motion.div key={d.label}
                initial={{ opacity: 0, y: 30, scale: 0.9 }}
                animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
                transition={{ delay: i * 0.15 + 0.8, type: "spring" }}
                whileHover={{ y: -6, scale: 1.03 }}
              >
                <div className="rounded-xl p-6 text-center h-full shadow-lg relative overflow-hidden" style={{ background: d.gradient }}>
                  <motion.div className="absolute inset-0 pointer-events-none"
                    style={{ background: 'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.08) 50%, transparent 70%)' }}
                    animate={{ x: ['-100%', '200%'] }}
                    transition={{ duration: 4, repeat: Infinity, delay: i * 0.5 + 1 }} />
                  <div className="absolute -top-4 -right-4 w-16 h-16 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }} />
                  <motion.div whileHover={{ scale: 1.15, rotate: 8 }}
                    className="w-11 h-11 rounded-2xl flex items-center justify-center mx-auto mb-3 relative z-10"
                    style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)' }}>
                    <d.icon className="w-5 h-5 text-white" />
                  </motion.div>
                  <h4 className="text-sm font-heading font-bold text-white mb-1 relative z-10">{d.label}</h4>
                  <p className="text-xs leading-relaxed relative z-10" style={{ color: 'rgba(255,255,255,0.7)' }}>{d.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      <style>{`
        @keyframes borderShift { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
      `}</style>
    </section>
  );
};
